// Propagates the Event Website's light/dark state into the Page-content
// iframes.
//
// Page content is rendered as static HTML inside a same-origin iframe
// (`/e/:id/desc/:desc_id`), so it never sees the parent's theme. Bobcat solves
// the equivalent problem on the Summary page by re-writing inline colours
// through the Froala editor API, which does not exist here. Instead this hands
// each frame to the shared `froala_theme_content` helper, which injects the same
// rules Bobcat's own editors get and toggles the theme class -- no stored HTML
// is touched and no inline styles are written.
//
// darkmode-js signals the theme by toggling `darkmode--activated` on <body>, so
// that class is the source of truth. It is observed rather than polled, which
// also picks up the toggle regardless of how it was flipped.
import applyFroalaThemeContent from './froala_theme_content';

const PARENT_CLASS = 'darkmode--activated';
const FRAME_SELECTOR = 'iframe.evw__page-frame';

const PageTheme = {
  start() {
    if (!document.querySelector(FRAME_SELECTOR)) {
      return;
    }

    this.sync();

    // Frames load asynchronously; a frame that finishes after start() still
    // needs the current theme. (Frames already loaded are covered by sync()
    // above -- their `load` has been and gone.)
    document.querySelectorAll(FRAME_SELECTOR).forEach((frame) => {
      frame.addEventListener('load', () => this.syncFrame(frame));
    });

    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  },

  isDark() {
    return document.body.classList.contains(PARENT_CLASS);
  },

  sync() {
    document.querySelectorAll(FRAME_SELECTOR).forEach((frame) => this.syncFrame(frame));
  },

  syncFrame(frame) {
    // Same-origin, so contentDocument is reachable. The shared helper injects
    // the rules and toggles the theme class -- identical policy to the Bobcat
    // editors, so all renderers of Page content agree.
    applyFroalaThemeContent(frame.contentDocument, this.isDark());
  },
};

export default PageTheme;
